import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  getDealById, getCurrentPIFSubmission, getAnalystReviews, getIICApprovals,
} from '../../services/dealService';
import { getAllApprovalSequences } from '../../services/firestoreService';
import PortalLayout from '../../components/shared/Layout/PortalLayout';
import IICApprovalForm from '../../components/forms/IICApprovalForm';
import PIFScoreCard from '../../components/pipeline/PIFScoreCard';
import AISummaryPanel from '../../components/pipeline/AISummaryPanel';
import FlagPanel from '../../components/pipeline/FlagPanel';
import Button from '../../components/shared/UI/Button';
import LoadingSpinner from '../../components/shared/UI/LoadingSpinner';
import { formatCurrency } from '../../utils/formatters';

export default function IICApprovalPage() {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [deal, setDeal] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [approvalSequence, setApprovalSequence] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const d = await getDealById(dealId);
    if (!d) { setLoading(false); return; }
    setDeal(d);
    const [s, revs, apps, seqs] = await Promise.all([
      getCurrentPIFSubmission(dealId),
      getAnalystReviews(dealId),
      getIICApprovals(dealId),
      getAllApprovalSequences(),
    ]);
    setSubmission(s);
    setReviews(revs);
    setApprovals(apps);
    setApprovalSequence(seqs.find(seq => seq.fund_id === d.fund_id));
    setLoading(false);
  }, [dealId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <PortalLayout><LoadingSpinner /></PortalLayout>;
  if (!deal) return <PortalLayout><div className="alert alert-error">Deal not found.</div></PortalLayout>;

  // Find the open approval matching this user's role. 'request_additional_information'
  // is treated as open so deals stranded by the old request-info write (which put the
  // raw decision into status) recover without a data migration. Decided steps write
  // 'approved' / 'deny' / 'suspend' and are never matched here.
  const pendingApproval = approvals.find(
    a => a.status === 'pending' || a.status === 'request_additional_information'
  );
  if (!pendingApproval) {
    return (
      <PortalLayout>
        <div className="alert alert-info">No pending IIC approval found for this deal.</div>
        <Button variant="secondary" onClick={() => navigate(`/pipeline/${dealId}`)}>← Back to Deal</Button>
      </PortalLayout>
    );
  }

  const approvalSequenceStages = approvalSequence?.stages || [];
  const currentStage = approvalSequenceStages.find(s => s.stage_order === pendingApproval.stage_order);
  if (currentStage && !(userProfile?.roles || [userProfile?.role]).includes(currentStage.role)) {
    return (
      <PortalLayout>
        <div className="alert alert-warning">
          This approval is waiting for the {currentStage.role?.replace(/_/g, ' ')}. It is not yet your turn.
        </div>
        <Button variant="secondary" onClick={() => navigate(`/pipeline/${dealId}`)}>← Back to Deal</Button>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div style={{ marginBottom: 16 }}>
        <Button variant="ghost" size="sm" onClick={() => navigate(`/pipeline/${dealId}`)}>← {deal.company_name}</Button>
      </div>
      <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>IIC Approval</h1>
      <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 24 }}>
        {deal.company_name} · Stage {pendingApproval.stage_order} of {approvalSequenceStages.length}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
        <div>
          <div className="card mb-4">
            <div className="card-header"><h2>Deal Overview</h2></div>
            <div className="card-body">
              {[
                ['Company', deal.company_name],
                ['Sector', deal.sector],
                ['Country', deal.company_country],
                ['Amount', formatCurrency(deal.requested_amount, deal.requested_currency)],
              ].map(([l, v]) => v ? (
                <div key={l} style={{ display: 'flex', gap: 10, marginBottom: 6 }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', minWidth: 80 }}>{l}</p>
                  <p style={{ fontSize: 13 }}>{v}</p>
                </div>
              ) : null)}
            </div>
          </div>
          <div className="mb-4"><PIFScoreCard submission={submission} rubric={null} /></div>
          {submission && <div className="mb-4"><AISummaryPanel submission={submission} dealId={dealId} /></div>}
          <FlagPanel reviews={reviews} />
          {reviews.map(r => (
            <div key={r.id} className="card mt-3">
              <div className="card-header">
                <h2>{r.review_type?.replace(/_/g, ' ')}</h2>
              </div>
              <div className="card-body">
                <p style={{ fontSize: 14, lineHeight: 1.7 }}>{r.notes}</p>
              </div>
            </div>
          ))}
        </div>

        <IICApprovalForm
          deal={deal}
          approvalRecord={pendingApproval}
          approvalSequence={approvalSequence}
          gate={pendingApproval.gate || 'pre_screening'}
          onSaved={() => navigate(`/pipeline/${dealId}`)}
          onDealUpdated={load}
        />
      </div>
    </PortalLayout>
  );
}
